import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NPUNIVERSE_APPS } from '../constants/apps';
import { saveEmailCapture } from '../lib/supabase';
import { EmailEvents } from '../ga4';

/**
 * Modal for capturing user email when they show interest in an app
 */
const EmailCaptureModal = ({ isOpen, onClose, primaryApp, secondaryApps = [], showCompatibleMessage = false }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [selectedSecondary, setSelectedSecondary] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Track modal opened
  useEffect(() => {
    if (isOpen && primaryApp) {
      EmailEvents.modalOpened(primaryApp, showCompatibleMessage);
    }
  }, [isOpen, primaryApp, showCompatibleMessage]);

  if (!isOpen) return null;

  const app = NPUNIVERSE_APPS.find(a => a.id === primaryApp);
  if (!app) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    // Save to Supabase
    const data = {
      email: email.toLowerCase().trim(),
      primaryApp,
      secondaryApps: selectedSecondary,
      timestamp: new Date().toISOString(),
      source: 'benchmark_result'
    };

    const result = await saveEmailCapture(data);

    if (result.success) {
      console.log('✅ Email captured successfully:', result.data);
      setIsSubmitted(true);
      
      // Track successful email capture
      EmailEvents.captured(primaryApp, true);
    } else {
      console.error('❌ Error capturing email:', result.error);
      setError(result.error || 'Error al guardar. Por favor, intenta de nuevo.');
    }

    setIsSubmitting(false);
  };

  const toggleSecondaryApp = (appId) => {
    setSelectedSecondary(prev =>
      prev.includes(appId)
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleBackToBenchmark = () => {
    setIsSubmitted(false);
    setEmail('');
    setSelectedSecondary([]);
    setError('');
    onClose();
  };
  
  const handleClose = () => {
    // Track dismissal only if email was not submitted
    if (!isSubmitted) {
      EmailEvents.dismissed();
    }
    handleBackToBenchmark();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {!isSubmitted ? (
          <>
            <h2 className="modal-title-green" style={{ marginBottom: '0.75rem', fontSize: '1.2rem' }}>
              {primaryApp === 'npu_check_ios'
                ? t('apps.reserve_access')
                : showCompatibleMessage && primaryApp === 'infinia_chat'
                  ? t('apps.reserve_access_compatible')
                  : `${t('apps.reserve_access')}: ${app.name} ${app.icon}`
              }
            </h2>

            <p style={{ marginBottom: '0.5rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.5, fontSize: '0.95rem' }}>
              {primaryApp === 'npu_check_ios'
                ? t('apps.ios_waitlist_message')
                : t('apps.early_access_message')
              }
            </p>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(0, 229, 255, 0.1)',
              border: '1px solid rgba(0, 229, 255, 0.2)',
              borderRadius: '50px',
              padding: '6px 14px',
              marginBottom: '1.5rem',
              marginTop: '0.25rem'
            }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--primary-cyan)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                🚀 {t('nav.soon')} — {app.launchQuarter}
              </span>
            </div>


            <form onSubmit={handleSubmit} autoComplete="off">
              <label style={{ display: 'block', marginBottom: '0.4rem', color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                {t('apps.confirm_email')}
              </label>
              <div className="email-input-wrapper">
                <input
                  type="text"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('apps.email_placeholder')}
                  required
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  name="email-capture-field"
                  id="email-capture-unique"
                  pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                  title="Introduce un email válido"
                  style={{
                    width: '100%',
                    padding: '0.65rem',
                    marginBottom: error ? '0.5rem' : '1rem',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                />
              </div>

              {error && (
                <p style={{ 
                  color: '#ff4444', 
                  fontSize: '0.85rem', 
                  marginBottom: '1rem',
                  padding: '0.5rem',
                  background: 'rgba(255, 68, 68, 0.1)',
                  borderRadius: '6px'
                }}>
                  ⚠️ {error}
                </p>
              )}

              {secondaryApps.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ marginBottom: '0.5rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                    {t('apps.also_notify_about')}
                  </p>
                  {secondaryApps.map(appId => {
                    const secondaryApp = NPUNIVERSE_APPS.find(a => a.id === appId);
                    if (!secondaryApp) return null;
                    
                    return (
                      <label
                        key={appId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.4rem 0.5rem',
                          marginBottom: '0.35rem',
                          cursor: 'pointer',
                          background: selectedSecondary.includes(appId) ? 'rgba(0, 255, 65, 0.1)' : 'transparent',
                          borderRadius: '6px',
                          transition: 'background 0.2s',
                          fontSize: '0.9rem'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedSecondary.includes(appId)}
                          onChange={() => toggleSecondaryApp(appId)}
                          style={{ width: '16px', height: '16px', flexShrink: 0 }}
                        />
                        <span>
                          {secondaryApp.icon} {t('apps.notify_when_ready', { app: secondaryApp.name, quarter: secondaryApp.launchQuarter })}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-premium-flip"
                style={{ width: '100%', marginBottom: '0.75rem' }}
              >
                {isSubmitting ? t('apps.submitting') : t('apps.confirm_reservation')}
              </button>

              <p style={{ textAlign: 'center', fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>
                ✓ {t('apps.no_spam')}
              </p>
            </form>

            <button
              onClick={onClose}
              style={{
                marginTop: '0.5rem',
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.6)',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              {t('apps.cancel')}
            </button>
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
            <h2 className="modal-title-green" style={{ marginBottom: '1rem' }}>
              {t('apps.success_title')}
            </h2>
            <p style={{ marginBottom: '1rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
              {t('apps.success_message', { email })}
            </p>
            <p style={{ marginBottom: '2rem', color: 'rgba(255,255,255,0.7)' }}>
              {t('apps.notify_when_ready_short', { app: app.name, quarter: app.launchQuarter })}
            </p>
            <button
              onClick={handleBackToBenchmark}
              className="btn-premium-flip"
              style={{ width: '100%' }}
            >
              {t('apps.back_to_benchmark')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailCaptureModal;
